<template>
  <div class="q-mt-lg col">
    <div class="user-goal">
      <c-page-section class="q-mb-lg">
        <q-card data-testid="user-vision-card">
          <q-card-section class="goal-card-section q-px-md">
            <div class="row items-center no-wrap">
              <div class="col">
                <div class="heading-bigger" data-testid="goal-heading">
                  {{ t('userGoals.vision') }}
                </div>
              </div>
              <div v-if="canEdit" class="col-auto row no-wrap items-center">
                <q-icon
                  data-testid="edit-vision"
                  :name="editable ? 'close' : 'edit'"
                  color="grey"
                  size="sn"
                  class="cursor-pointer q-pr-xs"
                  @click="() => toggleEditor()"
                >
                  <q-tooltip>
                    {{ editable ? t('userGoals.tooltip.closeVision') : t('userGoals.tooltip.editVision') }}
                  </q-tooltip>
                </q-icon>
              </div>
            </div>
          </q-card-section>
          <q-separator />
          <q-form class="q-gutter-md" @submit="onSubmit" @reset="close" @validation-error="validateForm">
            <q-card-section class="overflow-auto">
              <c-alert
                :show="userVision == undefined && !editable"
                type="info"
                data-testid="no-vision-alert"
                hide-close-button
                :message="t('userGoals.noVisionSet')"
              />
              <wysiwyg-editor-v2
                v-show="userVision !== undefined || editable"
                ref="editor"
                data-testid="vision-editor"
                :editable="editable"
                :height="editable ? '200px' : 'auto'"
                :smaller-headers="true"
                @ready="onEditorReady"
              />
              <div v-if="editable" class="row justify-end q-mt-md">
                <q-btn
                  class="ai-check-btn"
                  outline
                  icon="psychology"
                  :loading="isValidatingVision"
                  data-testid="validate-vision-button"
                  :label="t('userGoals.visionValidation.aiCheck')"
                  @click="onValidateVisionClick"
                />
              </div>
            </q-card-section>
            <q-card-section v-if="visionValidationResult" class="vision-validation-section">
              <ai-validation-result
                :score="visionValidationResult.score"
                :issues="visionValidationResult.issues"
                :suggestions="visionValidationResult.suggestions"
                :score-title="t('userGoals.visionValidation.scoreTitle')"
                :issues-title="t('userGoals.visionValidation.issuesTitle')"
                :suggestions-title="t('userGoals.visionValidation.suggestionsTitle')"
              />
            </q-card-section>
            <q-card-actions v-if="editable" data-testid="vision-edit-buttons">
              <c-dialog-form-btns
                :negative-label="t('cancelBtn')"
                :positive-label="t('submitBtn')"
                positive-data-testid="submit-vision-button"
                :positive-disabled="visionValidationResult?.can_save === false"
                @action:negative="() => toggleEditor(false)"
              />
            </q-card-actions>
          </q-form>
          <q-separator />
          <q-card-section v-if="userVision != undefined" class="vision-card-section">
            <last-change
              :date="
                formatDateForTemplate({
                  date: userVision.updatedAt,
                  showTime: true,
                })
              "
              :editor-name="userVision.editor.name"
            />
          </q-card-section>
        </q-card>
      </c-page-section>
    </div>
  </div>
</template>

<script lang="ts" setup>
import CPageSection from '@/components/_common/PageSection.vue';
import UserVisionModule, { UserVision } from '@/store/modules/UserVisionModule';
import { formatDateForTemplate } from '@/utils';
import { useI18n } from 'vue-i18n';
import WysiwygEditorV2 from '@/components/_common/WysiwygEditorV2.vue';
import CAlert from '@/components/_common/CommonAlert.vue';
import LastChange from '@/components/_common/LastChange.vue';
import { ref } from 'vue';
import CDialogFormBtns from '@/components/_common/DialogFormBtns.vue';
import AiValidationResult from '@/components/_common/AiValidationResult.vue';
import { useUserVision } from '@/composables/useUserVision';
import { useNotify, NotifyType } from '@/composables/useNotify';
import { hubClient } from '@tfpl/api-client';
/*****************************************************************************
 * PROPS
 ****************************************************************************/
const props = defineProps<{
  userVision?: UserVision;
  canEdit: boolean;
  selectedUserUuid: string;
}>();
/*****************************************************************************
 * SETUP
 ****************************************************************************/
const { t } = useI18n();
const {
  createVision: createVisionHook,
  editVision: editVisionHook,
  validateVision: validateVisionHook,
} = useUserVision();
const { notify } = useNotify();
const editable = ref<boolean>(false);
const formValid = ref(false);
const editor = ref<InstanceType<typeof WysiwygEditorV2>>();
const isValidatingVision = ref(false);

const onEditorReady = () => {
  editor.value?.setHTML(props.userVision?.message ?? '');
};

const toggleEditor = (active: boolean | undefined = undefined) => {
  editable.value = active ?? !editable.value;
  editor.value?.setHTML(props.userVision?.message ?? '');
  resetValidation();
};

const close = () => {
  editable.value = false;
  resetValidation();
};

const validateForm = () => {
  const message = editor.value?.getHTML() ?? '';
  if (message) {
    formValid.value = true;
  } else {
    formValid.value = false;
  }
};

const onSubmit = () => {
  validateForm();
  if (!formValid.value) {
    return;
  }
  const message = editor.value?.getHTML() ?? '';
  if (!props.userVision?.id) {
    createVisionHook({
      message,
      collaboratorUuid: props.selectedUserUuid,
    })
      .then(() => {
        UserVisionModule.fetchUserVision(props.selectedUserUuid);
      })
      .then(() => close());
  }
  if (props.userVision?.id) {
    editVisionHook({
      message,
      id: props.userVision.id,
    })
      .then(() => {
        UserVisionModule.fetchUserVision(props.selectedUserUuid);
      })
      .then(() => close());
  }
};

const severityOrder: Record<hubClient.ValidateVisionIssueDto['severity'], number> = {
  high: 3,
  medium: 2,
  low: 1,
} as const;

const sortIssues = (issues: hubClient.ValidateVisionIssueDto[]) => {
  return [...issues].sort((a, b) => {
    const right = severityOrder[b.severity] ?? 0;
    const left = severityOrder[a.severity] ?? 0;
    return right - left;
  });
};

const visionValidationResult = ref<hubClient.ValidateVisionResponseDto | null>();
const resetValidation = () => {
  visionValidationResult.value = null;
};
const onValidateVisionClick = async () => {
  isValidatingVision.value = true;
  visionValidationResult.value = null;
  try {
    const response = await validateVisionHook(editor.value!.getPlainText());
    visionValidationResult.value = {
      ...response,
      issues: sortIssues(response.issues ?? []),
    };
  } catch (e) {
    console.error('Error validating vision:', e);
    notify(t('common.requestError'), NotifyType.ERROR);
  } finally {
    isValidatingVision.value = false;
  }
};
</script>

<style lang="scss" scoped>
@import 'styles/app.variables.scss';

.vision-card-section {
  padding: 4px 21px;
}

.vision-validation-section {
  background-color: rgba(0, 0, 0, 0.02);
  border-radius: 8px;
}

.ai-check-btn {
  padding-left: 15px;
  padding-right: 15px;
}
</style>
